import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import PublicationImageWithLogo from '../components/PublicationImageWithLogo';
import { Button } from '../components/Button';
import { type Media } from '../api/media';
import { excerpt } from '../utils/text';

type Props = {
    agencyName: string | null;
    publicationTitle?: string;
    mediaUrl: string;
    extension: string;
    medias?: Media[];
    videoAsset?: string;
    isGlobal: boolean;
    networkIcon: string;
    content: string;
    link: string | null;
    onUseContent?: () => void;
};

type PostCardBlockProps = {
    agencyName: string | null;
    publicationTitle?: string;
    mediaUrl: string;
    extension: string;
    medias?: Media[];
    videoAsset?: string;
    isGlobal: boolean;
    networkIcon: string;
    textBlock: ReactNode;
    link: string | null;
    isFullText?: boolean;
};

const EMPTY_MEDIAS: Media[] = [];

export function PostCardBlock({
    agencyName,
    mediaUrl,
    extension,
    medias = EMPTY_MEDIAS,
    videoAsset,
    isGlobal,
    networkIcon,
    textBlock,
    link,
    isFullText = false,
}: PostCardBlockProps) {
    return (
        <div
            className={
                'w-full sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] bg-white rounded-md shadow-lg overflow-hidden'
            }
        >
            <span className="flex w-full h-[20px] sm:h-[24px] md:h-[28px] mt-2 mb-2 sm:mt-2.5 md:mt-3 px-1.5 sm:px-2 md:px-2.5 text-[9px] sm:text-[10px] md:text-xs font-light items-center">
                <span className="h-5 w-5 rounded-full bg-[#E0E0E0] mr-2" /> {agencyName}
            </span>
            <div className="relative h-[280px] w-full bg-light-gray sm:h-[320px] md:h-[360px] lg:h-[400px]">
                <PublicationImageWithLogo
                    imageUrl={mediaUrl}
                    alt="illustration"
                    className="flex h-full w-full items-center justify-center bg-light-gray"
                    isGlobal={isGlobal}
                    showLogo={true}
                    extension={extension}
                    medias={medias}
                    videoAsset={videoAsset}
                />
            </div>
            <img
                className="w-full px-1.5 sm:px-2 md:px-2.5 py-1 sm:py-1.5 md:py-2"
                src={networkIcon}
                alt="icons"
            />
            {isFullText ? (
                <div className="mb-2 block max-h-[min(50vh,22rem)] w-full overflow-y-auto whitespace-pre-wrap break-words px-1.5 pb-1 text-justify text-[9px] font-small text-dark-gray sm:mb-2.5 sm:px-2 sm:pb-1.5 sm:text-[10px] md:mb-3 md:px-2.5 md:pb-2 md:text-xs">
                    {textBlock}
                </div>
            ) : (
                <span
                    className="
                                block
                                w-full
                                px-1.5 sm:px-2 md:px-2.5
                                pb-1 sm:pb-1.5 md:pb-2
                                mb-2 sm:mb-2.5 md:mb-3
                                text-[9px] sm:text-[10px] md:text-xs
                                font-small
                                text-dark-gray
                                text-justify
                                overflow-hidden
                                text-ellipsis
                                line-clamp-2 md:line-clamp-3
                              "
                    style={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 'clamp(2, 3, 3)',
                    }}
                >
                    {textBlock}
                </span>
            )}
            {link && (
                <span className="block w-full px-1.5 sm:px-2 md:px-2.5 pb-1 sm:pb-1.5 md:pb-2 text-[9px] sm:text-[10px] md:text-xs font-small underline text-blue truncate">
                    {link}
                </span>
            )}
        </div>
    );
}

export function PublicationPreview({
    agencyName,
    publicationTitle,
    mediaUrl,
    extension,
    medias = EMPTY_MEDIAS,
    videoAsset,
    isGlobal,
    networkIcon,
    content,
    link,
    onUseContent,
}: Props) {
    const [showFullContent, setShowFullContent] = useState(false);
    const excerptText = useMemo(() => excerpt(content, 100), [content]);

    useEffect(() => {
        setShowFullContent(false);
    }, [content]);

    return (
        <div className="flex flex-1 flex-col items-center py-6 gap-1 sm:gap-2 md:gap-3 bg-light-gray rounded-md">
            <PostCardBlock
                agencyName={agencyName}
                publicationTitle={publicationTitle}
                mediaUrl={mediaUrl}
                extension={extension}
                medias={medias}
                videoAsset={videoAsset}
                isGlobal={isGlobal}
                networkIcon={networkIcon}
                link={link}
                isFullText={showFullContent}
                textBlock={showFullContent ? content : excerptText}
            />
            <div className="flex flex-col sm:flex-row w-full justify-center gap-2 sm:gap-3 md:gap-4 mt-2 sm:mt-3 md:mt-4">
                <Button isPrimary={false} onClick={() => setShowFullContent(v => !v)}>
                    {showFullContent ? 'Voir moins' : 'Voir plus'}
                </Button>
                <Button onClick={() => onUseContent?.()}>Utiliser ce contenu</Button>
            </div>
        </div>
    );
}
